import { nanoid } from 'nanoid';
import { immerable, produce } from 'immer';
import type { ApiClient, CancelTokenSource } from './client';
import type { ApiMethod, ResolverGeneric } from '@/shared/legacy/api/utils/api-memoize';
import { ApiMemoize } from '@/shared/legacy/api/utils/api-memoize';
import { CancelablePromise } from '@/shared/legacy/api/utils/cancelable-promise';
import { emitter } from '@/shared/lib/emitter';
import { logoutHelper } from '@/shared/legacy/lib/logout';

declare global {
  // eslint-disable-next-line ts/consistent-type-definitions
  interface Window {
    __API_BROADCAST_CHANNEL__: BroadcastChannel;
  }
}

if (!window.__API_BROADCAST_CHANNEL__) {
  window.__API_BROADCAST_CHANNEL__ = new BroadcastChannel('huntflow-bc-api');
}
const bc = window.__API_BROADCAST_CHANNEL__;

type APIEvent = {
  event: 'api-get';
  key: string;
  id: number;
} | {
  event: 'api-get:success';
  key: string;
  id: number;
  data: any;
} | {
  event: 'api-get:error';
  key: string;
  id: number;
  error: any;
};

const apiGetCache = new Map<string, {
  id: number;
  requestTime: number;
  data: {
    promise: Promise<unknown>;
    resolve: (arg0: any) => void;
    reject: (arg0: any) => void;
  };
}>();

// раз в 30 сек. запускается очистка кэша,
// которая удаляет записи старше чем 30 сек
// eslint-disable-next-line no-restricted-globals
setInterval(() => {
  const time = new Date().valueOf() - 30_000;
  for (const entry of apiGetCache.entries()) {
    if (entry[1].requestTime < time) {
      apiGetCache.delete(entry[0]);
    }
  }
}, 30_000);

const withResolvers = () => {
  let resolve: (arg0: any) => void;
  let reject: (arg0: any) => void;
  const promise = new CancelablePromise(
    new Promise((res, rej) => {
      resolve = (...args) => res(CancelablePromise.resolve(...args));
      reject = (...args) => rej(CancelablePromise.reject(...args));
    })
  );

  return {
    promise,
    // @ts-expect-error it's ok
    resolve,
    // @ts-expect-error it's ok
    reject
  };
};

bc.onmessage = (event: { data: APIEvent }) => {
  if (event.data.event === 'api-get') {
    apiGetCache.set(event.data.key, {
      id: event.data.id,
      requestTime: new Date().valueOf(),
      data: withResolvers()
    });
    return;
  }
  if (event.data.event === 'api-get:success') {
    const cacheEntry = apiGetCache.get(event.data.key);
    if (!cacheEntry || cacheEntry.id !== event.data.id) {
      return;
    }
    cacheEntry.data.resolve(event.data.data);
    return;
  }
  if (event.data.event === 'api-get:error') {
    const cacheEntry = apiGetCache.get(event.data.key);
    if (!cacheEntry || cacheEntry.id !== event.data.id) {
      return;
    }
    cacheEntry.data.reject(event.data.error);
  }
};

let visibility = !document.hidden;
emitter.on('app:visibilitychange', ({ data: v }) => {
  visibility = v;
});

export abstract class ApiLayer<Client extends ApiClient> {
  readonly client: Client;
  readonly cancelTokenSource?: CancelTokenSource;
  readonly [immerable]: boolean = true;

  constructor(apiClient: Client) {
    this.client = apiClient;

    return new Proxy(this, {
      get: (target, prop, receiver) => {
        const value = Reflect.get(target, prop, receiver);
        if (typeof value !== 'function') {
          return value;
        }

        const fn = (...args: any[]) => {
          const context = produce(target, (draft) => {
            draft.cancelTokenSource = this.client.createCancelTokenSource();
          });
          return value.apply(context, args);
        };
        Object.defineProperty(fn, 'name', {
          value: value.name,
          writable: false
        });
        return fn;
      }
    });
  }

  get methods(): ReturnType<Client['getMethods']> {
    type ParametersExceptFirst<F> = F extends (arg0: any, ...rest: infer R) => any ? R : never;

    if (!this.cancelTokenSource) {
      throw new Error('не указан cancelTokenSource');
    }

    const methods = this.client.getMethods(this.cancelTokenSource) as any;
    const oldGet = methods.get;

    const cacheableGet = (url: string, ...args: ParametersExceptFirst<typeof oldGet>) => {
      const params = (args[0] as any)?.params;
      const cacheKey = url + JSON.stringify(params);
      const id = nanoid();

      bc.postMessage({
        event: 'api-get',
        key: cacheKey,
        id
      });
      const promise = oldGet(url, ...args);
      promise.then((result: any) => {
        bc.postMessage({
          event: 'api-get:success',
          key: cacheKey,
          id,
          data: result
        });
      }).catch((e: any) => {
        logoutHelper.handleUnauthorized(e, 'get');
        bc.postMessage({
          event: 'api-get:error',
          key: cacheKey,
          id,
          error: e
        });
      });
      return promise;
    };

    const lazyGet = (url: string, ...args: ParametersExceptFirst<typeof oldGet>) => {
      const params = (args[0] as any)?.params;
      const cacheKey = url + JSON.stringify(params);
      // вкладка закрыта - пробуем дождаться запроса из активной вкладки
      // или любой первой (для этого есть небольшой разброс по времени ожидания в delay), которая его дёрнет
      if (!visibility) {
        const requestTime = new Date().valueOf();
        return delay().then(() => {
          const cacheEntry = apiGetCache.get(cacheKey);
          if (cacheEntry === undefined) {
            return cacheableGet(url, ...args);
          }
          if (Math.abs(requestTime - cacheEntry.requestTime) > 1_000) {
            apiGetCache.delete(cacheKey);
            return cacheableGet(url, ...args);
          }
          const promise = cacheEntry.data.promise;
          return promise;
        });
      }

      return cacheableGet(url, ...args);
    };

    return {
      ...methods,
      get: lazyGet
    };
  }

  toCancelablePromise<T>(value: T | PromiseLike<T>) {
    return CancelablePromise.resolve(value, this.cancelTokenSource?.cancel);
  }

  filterCanceledRequest<E = unknown>(exception: E): Promise<E> | undefined {
    if (!this.client.isCancel(exception)) {
      return Promise.reject(exception);
    }
  }

  memoizeMethod<M extends ApiMethod>(method: M, callback?: ResolverGeneric<M>): M {
    Object.defineProperty(method, 'name', {
      value: `${this.constructor.name}/${method.name}`,
      writable: false
    });
    const memoize = new ApiMemoize(method, callback);
    return function (this: any, ...params: Parameters<M>): ReturnType<M> {
      return memoize.dispatchWithContext(this, ...params) as ReturnType<M>;
    } as unknown as M;
  }
}

const rand = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;

function delay(min: number = 200, max: number = 500) {
  const ms = rand(min, max);
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}
